/**
 * Support entity — customer support tickets, their messages and the FAQ (ТЗ §8, §9).
 *
 * Mirrors the backend ``apps.support.serializers`` (Ticket list/detail, Message, FaqItem)
 * and the ``SupportCategory`` / ``SupportStatus`` choices. The server owns the ticket number,
 * status transitions and the frozen author identity; the client only renders these payloads.
 */
import type { BadgeTone } from "@melusine/shared-ui";

/** The five subjects a ticket can be filed under (matches ``SupportCategory``). */
export type SupportCategory = "shop" | "payment" | "bug" | "order" | "other";

/** Ticket lifecycle (operator-driven, matches ``SupportStatus``). */
export type SupportStatus = "new" | "in_progress" | "awaiting_user" | "closed";

/** Who authored a support message (matches ``SupportSenderRole``). */
export type SupportSenderRole = "customer" | "staff";

/** A single message in a ticket thread (mirrors ``MessageSerializer``). */
export interface SupportMessage {
  readonly id: number;
  readonly ticket: number;
  readonly sender_role: SupportSenderRole;
  readonly sender_display_name: string;
  readonly text: string;
  readonly attachment_url: string;
  readonly is_read: boolean;
  readonly created_at: string;
}

/** Compact ticket for the list endpoint (mirrors ``TicketListSerializer``). */
export interface SupportTicketListItem {
  readonly id: number;
  readonly number: string;
  readonly category: SupportCategory;
  readonly category_display: string;
  readonly status: SupportStatus;
  readonly status_display: string;
  readonly user_display_name: string;
  readonly last_message: string;
  readonly created_at: string;
  readonly last_message_at: string;
}

/** Full ticket detail incl. the conversation (mirrors ``TicketDetailSerializer``). */
export interface SupportTicketDetail {
  readonly id: number;
  readonly number: string;
  readonly category: SupportCategory;
  readonly category_display: string;
  readonly status: SupportStatus;
  readonly status_display: string;
  readonly user_display_name: string;
  readonly messages: readonly SupportMessage[];
  readonly created_at: string;
  readonly last_message_at: string;
}

/** A public FAQ entry (mirrors ``FaqItemSerializer``). */
export interface FaqItem {
  readonly id: number;
  readonly question: string;
  readonly answer: string;
  readonly sort_order: number;
}

/** A selectable support topic (label shown in the «выбор темы» picker, ТЗ §8). */
export interface SupportCategoryOption {
  readonly value: SupportCategory;
  readonly label: string;
}

/** The five topics offered when opening a ticket (ТЗ §8). */
export const SUPPORT_CATEGORY_OPTIONS: readonly SupportCategoryOption[] = [
  { value: "shop", label: "Вопросы по магазину" },
  { value: "payment", label: "Проблемы с оплатой" },
  { value: "bug", label: "Ошибка или сбой" },
  { value: "order", label: "Проблема с заказом" },
  { value: "other", label: "Другие вопросы" },
];

interface StatusPresentation {
  readonly label: string;
  readonly tone: BadgeTone;
}

/** Russian label + badge tone for each ticket status (ТЗ §8). */
export const SUPPORT_STATUS_PRESENTATION: Readonly<
  Record<SupportStatus, StatusPresentation>
> = {
  new: { label: "Новое", tone: "warning" },
  in_progress: { label: "В работе", tone: "amethyst" },
  awaiting_user: { label: "Ожидает пользователя", tone: "gold" },
  closed: { label: "Закрыто", tone: "neutral" },
};

/** Terminal statuses — a closed ticket's composer is disabled. */
export function isTicketClosed(status: SupportStatus): boolean {
  return status === "closed";
}
